import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  useTheme,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Card,
  Avatar,
  SvgIcon,
} from "@mui/material";
import { inject, observer } from "mobx-react";
import Store from "@/store";
import { UserStore } from "@/store/user";
import { Close } from "@mui/icons-material";
import Message from "./Message";
import { enqueueSnackbar } from "notistack";
import IChatOrder from "@/interfaces/chatOrder";
import IChatAppeal from "@/interfaces/chatAppeal";
import Viewer from "viewerjs";
import "viewerjs/dist/viewer.css";
import { stringAvatar } from "@/utility";
import { ReactComponent as UploadImageIcon } from "@/assets/icons/Input/Upload_Image.svg";
import { ReactComponent as SendIcon } from "@/assets/icons/Input/Send.svg";
import { allExtensionsString, imageFileTypes } from "@/config";

interface IChat {
  other?: { id: string; name: string };
  sendMessageId: string;
  chatMessages: IChatOrder[] | IChatAppeal[] | null;
  sendMessage: (
    id: string,
    content: string,
    participants: string[],
    message_type: "image" | "text" | "status",
  ) => Promise<void>;
  closeData: { isClosed: boolean; closeText: string };
  userStore?: UserStore;
}
const Chat = inject(Store.type.userStore)(
  observer(
    ({
      sendMessageId,
      other,
      chatMessages,
      sendMessage,
      closeData,
      userStore,
    }: IChat) => {
      const theme = useTheme();

      const [firstVisit, setFirstVisit] = useState(true);
      const [isSubmiting, setIsSubmiting] = useState(false);
      const chatContainerRef = useRef<HTMLUListElement | null>(null);
      const scrollableContainerRef = useRef<HTMLElement | null>(null);
      const [inputFile, setInputFile] = useState<File | null>(null);
      const [message, setMessage] = useState("");
      const bottomChat = useRef<HTMLInputElement | null>(null);
      const isScrolling = useRef<boolean>(false);
      const isBrowserScrolling = useRef<boolean>(false);
      const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

      const scrollTimeoutDelay = 5000;

      const handleScroll = () => {
        if (!isBrowserScrolling.current) {
          isScrolling.current = true;

          scrollTimeout.current && clearTimeout(scrollTimeout.current);
          scrollTimeout.current = setTimeout(() => {
            isScrolling.current = false;
          }, scrollTimeoutDelay);
        }
      };
      const handleScrollEnd = () => {
        isBrowserScrolling.current = false;
      };
      useEffect(() => {
        if (scrollableContainerRef.current) {
          scrollableContainerRef.current.addEventListener(
            "scroll",
            handleScroll,
          );
          scrollableContainerRef.current.addEventListener(
            "scrollend",
            handleScrollEnd,
          );
        }

        return () => {
          scrollTimeout.current && clearTimeout(scrollTimeout.current);
          if (scrollableContainerRef.current) {
            scrollableContainerRef.current.removeEventListener(
              "scroll",
              handleScroll,
            );
            scrollableContainerRef.current.removeEventListener(
              "scrollend",
              handleScrollEnd,
            );
          }
        };
      }, []);

      useEffect(() => {
        let viewer: Viewer;
        if (chatContainerRef.current) {
          viewer = new Viewer(chatContainerRef.current);
        }
        return () => {
          if (viewer) {
            viewer.destroy();
          }
        };
      }, [chatMessages]);

      useEffect(() => {
        if (
          bottomChat.current &&
          chatMessages &&
          (chatMessages[chatMessages.length - 1]?.created_by ===
            userStore?.userId ||
            firstVisit)
        ) {
          bottomChat.current.scrollIntoView({ behavior: "smooth" });
          setFirstVisit(false);
        }
      }, [chatMessages]);

      useEffect(() => {
        if (
          bottomChat.current &&
          chatMessages &&
          (chatMessages[chatMessages.length - 1]?.created_by ===
            userStore?.userId ||
            !isScrolling.current)
        ) {
          isBrowserScrolling.current = true;
          bottomChat.current &&
            bottomChat.current.scrollIntoView({ behavior: "smooth" });
        }
      }, [chatMessages]);

      const [base64String, setBase64String] = useState<string | null>(null);
      const handleFileInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
      ) => {
        setIsSubmiting(true);
        if (e.target.files && e.target.files.length > 0) {
          const selectedFile = e.target.files[0];

          if (!imageFileTypes.includes(selectedFile.type)) {
            enqueueSnackbar(
              `Please select an image file. (${allExtensionsString})`,
              {
                variant: "error",
              },
            );
            return;
          }
          //validate file size < 500kb
          if (selectedFile.size > 500000) {
            enqueueSnackbar("File size limit: 500kb", {
              variant: "error",
            });
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            setBase64String(reader.result as string);
            e.target.value = "";
          };

          setInputFile(selectedFile);
          reader.readAsDataURL(selectedFile);
          setMessage("");
        }

        setIsSubmiting(false);
      };
      const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleMessageSubmit();
        }
      };

      const handleMessageSubmit = async () => {
        setIsSubmiting(true);
        if (
          userStore?.userId &&
          (base64String || message) &&
          sendMessage &&
          other
        ) {
          const content = base64String ? base64String : message;
          const type = base64String ? "image" : "text";
          sendMessage(
            sendMessageId,
            content,
            [userStore.userId, other.id],
            type,
          );
        }
        setMessage("");
        setInputFile(null);
        setBase64String(null);
        setIsSubmiting(false);
      };

      const handleClearFile = () => {
        setInputFile(null);
        setBase64String(null);
        setMessage("");
      };

      return (
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            minHeight: "100%",
            height: `calc(100vh - ${theme.footerHeight} - ${theme.navbarHeight})`,
            py: "0",
            px: { sm: "6.941431670281996%", md: "0" },
            "&:before": {
              borderRadius: "0",
            },
            "&.MuiPaper-root": {
              background: "rgba(255, 255, 255, 0.6)",
              borderRadius: "0",
            },
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            gap="24px"
            height="48px"
            pt="13.7px"
            pb="15.3px"
            px="34px"
            boxSizing="content-box"
            bgcolor={theme.colors.blueDark}
          >
            {other && (
              <>
                <Avatar
                  {...stringAvatar(other.name ?? "", {
                    width: "48px",
                    height: "48px",
                  })}
                ></Avatar>
                <Typography
                  variant="body3"
                  color={theme.colors.lightFontColor}
                  fontWeight="600"
                  letterSpacing="0.5px"
                >
                  {other.name}
                </Typography>
                {/* <Button>View Profile</Button> */}
              </>
            )}
          </Stack>
          <Box
            ref={scrollableContainerRef}
            flexGrow="1"
            textAlign="center"
            px="14px"
            pt="9.34px"
            pb="6.81px"
            overflow="auto"
          >
            {chatMessages && userStore?.profile && other && (
              <List ref={chatContainerRef} sx={{ py: "0" }}>
                {chatMessages.map((msg: IChatOrder | IChatAppeal, index) => {
                  const datetime = msg.created_at;
                  const date = new Date(datetime);
                  const formattedDate =
                    date.getDate() === Date.now()
                      ? `${date.toLocaleDateString()} ${date.toLocaleTimeString(
                          [],
                          {
                            hour: "numeric",
                            minute: "numeric",
                          },
                        )}`
                      : date.toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "numeric",
                        });
                  let profile: {
                    name: string;
                    side: "left" | "right";
                  } | null = null;
                  if (
                    index === 0 ||
                    (index !== 0 &&
                      msg.created_by !== chatMessages[index - 1].created_by)
                  ) {
                    if (msg.created_by === userStore.userId) {
                      profile = {
                        name: userStore.fullname ?? "",
                        side: "right",
                      };
                    } else {
                      profile = {
                        name: other.name,
                        side: "left",
                      };
                    }
                  }
                  return (
                    <React.Fragment key={msg.message_id}>
                      {profile && (
                        <Stack
                          direction={
                            profile.side === "left" ? "row" : "row-reverse"
                          }
                          gap="24px"
                          height="48px"
                          mt={index === 0 ? "0" : "40px"}
                          sx={{ float: profile.side }}
                        >
                          <Avatar
                            {...stringAvatar(profile.name ?? "", {
                              width: "48px",
                              height: "48px",
                            })}
                          ></Avatar>
                          <Stack
                            direction="column"
                            gap="2px"
                            height="48px"
                            justifyContent="flex-start"
                            alignItems={
                              profile.side === "left"
                                ? "flex-start"
                                : "flex-end"
                            }
                          >
                            <Typography
                              variant="body3"
                              fontWeight="600"
                              letterSpacing="0.5px"
                            >
                              {profile.name}
                            </Typography>
                            <Typography
                              variant="body5"
                              fontWeight="500"
                              letterSpacing="0.5px"
                            >
                              {formattedDate}
                            </Typography>
                          </Stack>
                        </Stack>
                      )}
                      <ListItem sx={{ p: "0", pt: "25px" }}>
                        {msg.message_type === "image" ? (
                          <Message
                            side={
                              userStore.userId === msg.created_by
                                ? "right"
                                : "left"
                            }
                            src={msg.content}
                          ></Message>
                        ) : msg.message_type === "status" ? (
                          <Message side="center">{msg.content}</Message>
                        ) : (
                          <Message
                            side={
                              userStore.userId === msg.created_by
                                ? "right"
                                : "left"
                            }
                          >
                            {msg.content}
                          </Message>
                        )}
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
            <Box ref={bottomChat}></Box>
          </Box>
          <Box
            height="fit-content"
            mx="14px"
            mt="29.19px"
            mb="41.67px"
            borderRadius="48px"
            border="1px solid #BEC6D6"
          >
            <Stack
              direction="row"
              alignItems="center"
              my="7px"
              mx="8px"
              height="56px"
            >
              {!closeData.isClosed ? (
                <>
                  <TextField
                    type="file"
                    inputProps={{
                      id: "file-input",
                      onChange: handleFileInputChange,
                      accept: imageFileTypes.join(", "),
                    }}
                    sx={{ display: "none" }}
                  />
                  <IconButton
                    component="label"
                    htmlFor="file-input"
                    sx={{
                      width: "auto",
                      height: "100%",
                      aspectRatio: "1/1",
                      p: "0",
                      bgcolor: "#ECF0F8",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <SvgIcon
                      component={UploadImageIcon}
                      sx={{
                        width: "50%",
                        height: "50%",
                        fill: "none",
                      }}
                      inheritViewBox
                    ></SvgIcon>
                  </IconButton>
                  <TextField
                    variant="outlined"
                    fullWidth
                    type="text"
                    placeholder="Type Your Message Here . . ."
                    value={inputFile ? inputFile.name : message}
                    disabled={!!inputFile}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                      endAdornment: (
                        <>
                          {inputFile && (
                            <IconButton onClick={handleClearFile}>
                              <Close />
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                    sx={{
                      "&.MuiFormControl-root": {
                        mt: "0",
                      },
                      "& .MuiInputBase-input, & input.MuiInputBase-input.Mui-disabled":
                        {
                          pl: "16.22px",
                          pr: inputFile ? "0" : "1.375rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontWeight: "500",
                          lineHeight: "24px",
                          letterSpacing: "0.3px",
                          WebkitTextFillColor: "initial",
                        },
                      "& .MuiOutlinedInput-notchedOutline": {
                        display: "none",
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleMessageSubmit}
                    disabled={isSubmiting}
                    sx={{
                      width: "auto",
                      height: "100%",
                      aspectRatio: "1/1",
                      p: "0",
                      bgcolor: theme.colors.blueDark,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        bgcolor: theme.colors.blueDark,
                        cursor: !(message || inputFile)
                          ? "not-allowed"
                          : "pointer",
                      },
                    }}
                  >
                    <SvgIcon
                      component={SendIcon}
                      sx={{
                        width: "37.5%",
                        height: "37.5%",
                      }}
                      inheritViewBox
                    ></SvgIcon>
                  </IconButton>
                </>
              ) : (
                <Box mx="auto">
                  <Typography textAlign="center">
                    {closeData.closeText}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
          <Typography
            variant="body2"
            style={{
              width: "100%",
              marginTop: "-20px",
            }}
            textAlign="center"
          >
            (File size limit: 500kb)
          </Typography>
        </Card>
      );
    },
  ),
);

export default Chat;
